import { Component, computed, effect, inject, input, output, signal, ViewChild } from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { CommonModule } from '@angular/common';
import { FormBuilder, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';
import { FontAwesomeModule } from '@fortawesome/angular-fontawesome';

import { OrganizationModel } from '../../../../core/models/data/organization.model';
import {
    OrganizationMessageSuppressionModel,
    UpdateOrganizationMessageSuppressionRequest
} from '../../../../core/models/data/organization-message-suppression.model';
import { OrganizationMessageSuppressionService } from '../../../../core/services/organization-message-suppression.service';
import { BootstrapService } from '../../../../core/services/bootstrap.service';
import { CustomValidators } from '../../../../core/validators/custom-validators';
import { FormValidatorMessages } from '../../../../core/enums/forms.enums';
import { ModalActions, ModalTypes } from '../../../../core/enums/base.enums';
import { ConfirmationModalComponent } from '../../../../parts/confirmation-modal/confirmation-modal.component';
import { InfoModalComponent } from '../../../../parts/info-modal/info-modal.component';
import { InputLabel } from '../../../../parts/input-label/input-label.component';
import { MessageSuppressionMessagingConstants } from '../../../../core/constants/messaging.constants';

@Component({
    selector: 'ms-organization-message-suppression-settings',
    standalone: true,
    imports: [
        CommonModule,
        ReactiveFormsModule,
        FontAwesomeModule,
        ConfirmationModalComponent,
        InfoModalComponent,
        InputLabel
    ],
    templateUrl: './organization-message-suppression-settings.component.html',
    styleUrls: ['./organization-message-suppression-settings.component.scss']
})
export class OrganizationMessageSuppressionSettingsComponent {

    private fb = inject(FormBuilder);
    private bootstrapService = inject(BootstrapService);
    private messageSuppressionService = inject(OrganizationMessageSuppressionService);

    public organization = input.required<OrganizationModel>();
    public suppressionUpdated = output<OrganizationMessageSuppressionModel>();

    public FormValidatorMessages = FormValidatorMessages;

    public suppressionForm: FormGroup;
    public isLoading = signal<boolean>(true);
    public isSubmitting = signal<boolean>(false);
    public errorMessage = signal<string | undefined>(undefined);
    public currentSuppression = signal<OrganizationMessageSuppressionModel | undefined>(undefined);

    private formValues;

    public overrideEnabled = computed(() => this.formValues()?.overrideEmailRecipient === true);

    public hasFormBeenModified = computed(() => {
        const current = this.currentSuppression();
        const values = this.formValues();
        if (!current || !values) return false;
        return current.suppressMessages !== values.suppressMessages
            || current.overrideEmailRecipient !== values.overrideEmailRecipient
            || (current.overrideEmailAddress ?? '') !== (values.overrideEmailAddress ?? '');
    });

    public showActiveBanner = computed(() => {
        const current = this.currentSuppression();
        return !!current && (current.suppressMessages === true || current.overrideEmailRecipient === true);
    });

    @ViewChild('messageSuppressionConfirmationModalRef') confirmationModalRef!: ConfirmationModalComponent;
    @ViewChild('messageSuppressionInfoModalRef') infoModalRef!: InfoModalComponent;

    private pendingRequest: UpdateOrganizationMessageSuppressionRequest | undefined;

    constructor() {
        this.suppressionForm = this.fb.group({
            suppressMessages: [false],
            overrideEmailRecipient: [false],
            overrideEmailAddress: ['', [CustomValidators.email]]
        });

        this.formValues = toSignal(this.suppressionForm.valueChanges, { initialValue: this.suppressionForm.value });

        effect(() => {
            const enabled = this.overrideEnabled();
            const control = this.suppressionForm.get('overrideEmailAddress');
            if (!control) return;
            if (enabled) {
                control.addValidators(Validators.required);
            } else {
                control.removeValidators(Validators.required);
            }
            control.updateValueAndValidity({ emitEvent: false });
        });

        effect(() => {
            const org = this.organization();
            if (org?.organizationId) {
                this.loadSuppression(org.organizationId);
            }
        });
    }

    private async loadSuppression(organizationId: number): Promise<void> {
        this.isLoading.set(true);
        this.errorMessage.set(undefined);
        const result = await this.messageSuppressionService.getMessageSuppression(organizationId);
        if (result.success && result.data) {
            this.applyLoadedState(result.data);
        } else {
            this.errorMessage.set(result.message ?? MessageSuppressionMessagingConstants.MESSAGE_SUPPRESSION_LOAD_FAILURE());
        }
        this.isLoading.set(false);
    }

    private applyLoadedState(model: OrganizationMessageSuppressionModel): void {
        this.currentSuppression.set(model);
        this.suppressionForm.patchValue({
            suppressMessages: model.suppressMessages,
            overrideEmailRecipient: model.overrideEmailRecipient,
            overrideEmailAddress: model.overrideEmailAddress ?? ''
        }, { emitEvent: true });
        this.suppressionForm.markAsPristine();
    }

    public onResetClicked(): void {
        const current = this.currentSuppression();
        if (!current) return;
        this.applyLoadedState(current);
    }

    public onSuppressMessagesToggled(): void {
        if (this.suppressionForm.value.suppressMessages === true
            && this.suppressionForm.value.overrideEmailRecipient === true) {
            this.suppressionForm.patchValue({
                overrideEmailRecipient: false,
                overrideEmailAddress: ''
            });
        }
    }

    public onOverrideEmailRecipientToggled(): void {
        const overrideOn = this.suppressionForm.value.overrideEmailRecipient === true;
        if (overrideOn && this.suppressionForm.value.suppressMessages === true) {
            this.suppressionForm.patchValue({ suppressMessages: false });
        }
        if (!overrideOn) {
            this.suppressionForm.patchValue({ overrideEmailAddress: '' });
            this.suppressionForm.get('overrideEmailAddress')?.markAsUntouched();
        }
    }

    public onSaveClicked(): void {
        if (this.suppressionForm.invalid) {
            this.suppressionForm.markAllAsTouched();
            return;
        }

        const current = this.currentSuppression();
        const values = this.suppressionForm.value;
        const overrideEmail = values.overrideEmailRecipient
            ? (values.overrideEmailAddress?.trim() || undefined)
            : undefined;

        const request: UpdateOrganizationMessageSuppressionRequest = {
            suppressMessages: !!values.suppressMessages,
            overrideEmailRecipient: !!values.overrideEmailRecipient,
            overrideEmailAddress: overrideEmail
        };

        const enablingSuppression = request.suppressMessages && !current?.suppressMessages;
        const enablingOverride = request.overrideEmailRecipient && !current?.overrideEmailRecipient;

        if (enablingSuppression || enablingOverride) {
            this.pendingRequest = request;
            this.openConfirmation(enablingSuppression, enablingOverride, overrideEmail);
            return;
        }

        this.executeSave(request);
    }

    private openConfirmation(enablingSuppression: boolean, enablingOverride: boolean, overrideEmail: string | undefined): void {
        this.confirmationModalRef.modalType.set(ModalTypes.Exclamation);

        if (enablingSuppression) {
            this.confirmationModalRef.message.set(
                MessageSuppressionMessagingConstants.MESSAGE_SUPPRESSION_ENABLE_CONFIRMATION_BODY()
            );
            this.confirmationModalRef.confirmButtonText.set('Enable Suppression');
        } else if (enablingOverride) {
            const body = MessageSuppressionMessagingConstants.MESSAGE_SUPPRESSION_OVERRIDE_CONFIRMATION_BODY()
                .replaceValuesWithObject({ overrideEmailAddress: overrideEmail ?? '' });
            this.confirmationModalRef.message.set(body);
            this.confirmationModalRef.confirmButtonText.set('Enable Override');
        }

        this.bootstrapService.manageModal('#messageSuppressionConfirmationModal', ModalActions.Show);
    }

    public async onConfirmationConfirmed(): Promise<void> {
        const request = this.pendingRequest;
        this.pendingRequest = undefined;
        this.bootstrapService.manageModal('#messageSuppressionConfirmationModal', ModalActions.Hide);
        if (!request) return;
        await this.executeSave(request);
    }

    public onConfirmationCancelled(): void {
        this.pendingRequest = undefined;
        this.bootstrapService.manageModal('#messageSuppressionConfirmationModal', ModalActions.Hide);
    }

    private async executeSave(request: UpdateOrganizationMessageSuppressionRequest): Promise<void> {
        const organizationId = this.organization().organizationId;
        this.isSubmitting.set(true);
        try {
            const result = await this.messageSuppressionService.updateMessageSuppression(organizationId, request);
            if (result.success && result.data) {
                this.applyLoadedState(result.data);
                this.suppressionUpdated.emit(result.data);
                this.infoModalRef.modalType.set(ModalTypes.Success);
                this.infoModalRef.message.set(
                    result.message ?? MessageSuppressionMessagingConstants.MESSAGE_SUPPRESSION_SAVE_SUCCESS()
                );
            } else {
                this.infoModalRef.modalType.set(ModalTypes.Failure);
                this.infoModalRef.message.set(
                    result.message ?? MessageSuppressionMessagingConstants.MESSAGE_SUPPRESSION_SAVE_FAILURE()
                );
            }
            this.bootstrapService.manageModal('#messageSuppressionInfoModal', ModalActions.Show);
        } finally {
            this.isSubmitting.set(false);
        }
    }
}
